import * as Sentry from '@sentry/vue'
import { pwaConfig } from './pwaTools'
import { sentryCountMetric } from './sentryMetrics.ts'
import type { SwAuthTelemetryEvent } from './swAuthTelemetry.ts'

const SW_AUTH_ISSUE_TAG = 'sw_auth.issue'

function captureSwAuthMessage(
  message: string,
  level: Sentry.SeverityLevel,
  fingerprint: string[],
  tags: Record<string, string>,
  extra?: Record<string, unknown>,
): void {
  Sentry.withScope((scope) => {
    scope.setLevel(level)
    scope.setFingerprint(fingerprint)
    for (const [key, value] of Object.entries(tags)) {
      scope.setTag(key, value)
    }
    if (extra) scope.setExtras(extra)
    Sentry.captureMessage(message, level)
  })
}

/** Maps SW auth telemetry + counters to Sentry messages, breadcrumbs, and metrics. */
export function reportSwAuthTelemetryToSentry(event: SwAuthTelemetryEvent): void {
  const commonExtra = {
    swAuthMetrics: event.metrics,
    displayMode: pwaConfig.displayMode,
    browserInfoName: pwaConfig.browserInfo.name,
    browserInfoOs: pwaConfig.browserInfo.os,
    swController: Boolean(
      typeof navigator !== 'undefined' && navigator.serviceWorker?.controller,
    ),
  }

  switch (event.kind) {
    case 'sync_posted':
      if (event.hasEpoch && event.preflightReadFailed) {
        captureSwAuthMessage(
          'SW auth sync: main-thread IDB preflight read failed; posting SW_AUTH_SYNC anyway',
          'warning',
          ['sw-auth-main-preflight-read-failed'],
          {
            [SW_AUTH_ISSUE_TAG]: 'main_preflight_read_failed',
          },
          { ...commonExtra, waitMs: event.waitMs, matched: event.matched },
        )
      } else if (event.hasEpoch && !event.matched) {
        captureSwAuthMessage(
          'SW auth sync: IndexedDB preflight timed out before notify',
          'warning',
          ['sw-auth-idb-preflight-timeout'],
          {
            [SW_AUTH_ISSUE_TAG]: 'idb_preflight_timeout',
            'sw_auth.preflight_matched': 'false',
          },
          { ...commonExtra, waitMs: event.waitMs },
        )
      }
      sentryCountMetric('sw_auth.sync_posted')
      if (event.hasEpoch) {
        sentryCountMetric(event.matched ? 'sw_auth.preflight_matched' : 'sw_auth.preflight_timeout')
      }
      break
    case 'reload_ok':
      if (event.attempt > 0) {
        Sentry.addBreadcrumb({
          category: 'sw_auth',
          message: 'SW auth reload matched epoch after retry',
          level: 'info',
          data: { attempt: event.attempt },
        })
      }
      sentryCountMetric('sw_auth.reload_ok')
      break
    case 'reload_mismatch_retry':
      Sentry.addBreadcrumb({
        category: 'sw_auth',
        message: `SW auth reload epoch mismatch (${event.mismatchKind}); retrying`,
        level: 'warning',
        data: { attempt: event.attempt, mismatchKind: event.mismatchKind, ...commonExtra },
      })
      sentryCountMetric('sw_auth.reload_mismatch_retry')
      break
    case 'reload_mismatch_exhausted':
      captureSwAuthMessage(
        event.mismatchKind === 'issued_at'
          ? 'SW auth sync: reload IDB session JWT iat older than main issuedAtMs epoch'
          : 'SW auth sync: reload from IDB did not match expected uid after retries',
        'error',
        [
          event.mismatchKind === 'issued_at'
            ? 'sw-auth-reload-issued-at-mismatch-exhausted'
            : 'sw-auth-reload-uid-mismatch-exhausted',
        ],
        {
          [SW_AUTH_ISSUE_TAG]:
            event.mismatchKind === 'issued_at'
              ? 'reload_issued_at_mismatch_exhausted'
              : 'reload_uid_mismatch_exhausted',
          'sw_auth.mismatch_kind': event.mismatchKind,
        },
        commonExtra,
      )
      sentryCountMetric('sw_auth.reload_mismatch_exhausted')
      break
    case 'reload_without_epoch':
      sentryCountMetric('sw_auth.reload_without_epoch')
      break
    case 'idb_read_failed':
      captureSwAuthMessage(
        'SW auth: transient IndexedDB read failed during logout guard (fail-open)',
        'warning',
        ['sw-auth-idb-read-failed'],
        {
          [SW_AUTH_ISSUE_TAG]: 'idb_read_failed',
        },
        { ...commonExtra, message: event.message },
      )
      sentryCountMetric('sw_auth.idb_read_failed')
      break
    default:
      break
  }
}
